var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Delegate1, MulticastDelegate1 } from "flavr-common";
import * as sdpTransform from "sdp-transform";
import { preferencesService } from "../../preferences/PreferencesService";
import { RemoteSdp } from "./sdp/RemoteSdp";
import { getSendingRtpParameters } from "./Ortc";
import { preserveLiveViewResolution } from "./LiveViewQuality";
const SCTP_NUM_STREAMS = { OS: 1024, MIS: 1024 };
function extractRtpCapabilities(sdpObject) {
    const codecsMap = new Map();
    const headerExtensions = [];
    let gotAudio = false;
    let gotVideo = false;
    for (const m of sdpObject.media) {
        const kind = m.type;
        switch (kind) {
            case "audio": {
                if (gotAudio)
                    continue;
                gotAudio = true;
                break;
            }
            case "video": {
                if (gotVideo)
                    continue;
                gotVideo = true;
                break;
            }
            default:
                continue;
        }
        for (const rtp of m.rtp) {
            if (!rtp.payload)
                continue;
            const codec = {
                kind,
                mimeType: `${kind}/${rtp.codec}`,
                preferredPayloadType: rtp.payload,
                clockRate: rtp.rate,
                channels: rtp.encoding,
                parameters: {},
                rtcpFeedback: []
            };
            codecsMap.set(rtp.payload, codec);
        }
        for (const fmtp of m.fmtp || []) {
            const parameters = sdpTransform.parseParams(fmtp.config);
            const codec = codecsMap.get(fmtp.payload);
            if (!codec)
                continue;
            if (parameters && parameters.hasOwnProperty("profile-level-id")) {
                parameters["profile-level-id"] = String(parameters["profile-level-id"]);
            }
            codec.parameters = parameters;
        }
        for (const fb of m.rtcpFb || []) {
            const codec = codecsMap.get(fb.payload);
            if (!codec)
                continue;
            codec.rtcpFeedback.push({
                type: fb.type,
                parameter: fb.subtype
            });
        }
        for (const ext of m.ext || []) {
            // Ignore encrypted extensions
            if (ext["encrypt-uri"])
                continue;
            headerExtensions.push({
                kind,
                uri: ext.uri,
                preferredId: ext.value
            });
        }
    }
    return {
        codecs: Array.from(codecsMap.values()),
        headerExtensions
    };
}
export function clone(data, defaultValue) {
    if (typeof data === "undefined")
        return defaultValue;
    return JSON.parse(JSON.stringify(data));
}
export class Handler {
    constructor() {
        this.transportReady = false;
        this.mapMidTransceiver = new Map();
        this.onConnectDelegate = new MulticastDelegate1();
        this.onConnectionstatechangeDelegate = new MulticastDelegate1();
        this.onNegotiationNeededDelegate = new Delegate1();
        this.onIcecandidateDelegate = new Delegate1();
        this.onTrackDelegate = new Delegate1();
        this.pendingIceCandidates = [];
        this.restartingIce = false;
        this.iceCompleted = false;
        this.mapTracks = new Map();
        this.onTrack = (event) => {
            const { streams, receiver, track, transceiver } = event;
            console.log(`onTrack: mid: ${event.transceiver.mid} dir: ${event.transceiver.direction} curDir: ${event.transceiver.currentDirection}`, event.streams.length, this.peerConnection ? this.peerConnection.signalingState : undefined);
            if (!transceiver.mid)
                return;
            if (!streams)
                return;
            if (streams.length === 0)
                return;
            const stream = streams[0];
            this.mapTracks.set(transceiver.mid, {
                stream,
                track
            });
            this.onTrackDelegate.invoke({
                stream,
                track,
                rtpReceiver: receiver,
                transceiver
            });
        };
    }
    init({ direction, iceParameters, iceCandidates, dtlsParameters, sctpParameters, iceServers, iceTransportPolicy, additionalSettings, extendedRtpCapabilities }) {
        this.direction = direction;
        this.remoteSdp = new RemoteSdp(iceParameters, iceCandidates, dtlsParameters, sctpParameters);
        this.sendingRtpParametersByKind = {
            audio: getSendingRtpParameters("audio", extendedRtpCapabilities),
            video: getSendingRtpParameters("video", extendedRtpCapabilities)
        };
        this.peerConnection = new RTCPeerConnection(Object.assign({ iceServers: iceServers || [], iceTransportPolicy: iceTransportPolicy || "all" }, additionalSettings));
        this.peerConnection.onconnectionstatechange = () => {
            if (!this.peerConnection) {
                return;
            }
        };
        this.peerConnection.oniceconnectionstatechange = () => {
            if (!this.peerConnection) {
                return;
            }
            switch (this.peerConnection.iceConnectionState) {
                case "checking":
                    this.onConnectionstatechangeDelegate.invoke("connecting");
                    break;
                case "connected":
                case "completed":
                    if (this.restartingIce) {
                        console.info("[multiplayer] ICE connection connected after failed");
                        this.restartingIce = false;
                    }
                    this.iceCompleted = true;
                    this.onConnectionstatechangeDelegate.invoke("connected");
                    break;
                case "failed":
                    if (this.iceCompleted) {
                        console.warn("[multiplayer] ICE connection failed after success");
                    }
                    else {
                        console.warn("[multiplayer] ICE connection failed before success");
                    }
                    this.onConnectionstatechangeDelegate.invoke("failed");
                    break;
                case "disconnected":
                    this.onConnectionstatechangeDelegate.invoke("disconnected");
                    break;
                case "closed":
                    this.onConnectionstatechangeDelegate.invoke("closed");
                    break;
            }
        };
        this.peerConnection.onnegotiationneeded = () => {
            if (!this.restartingIce && this.direction !== "send")
                return;
            this.onNegotiationNeededDelegate.invoke(this.restartingIce);
        };
        this.peerConnection.onicecandidate = (event) => {
            if (!event.candidate)
                return;
            if (this.filterIceCandidate(event.candidate))
                return;
            this.onIcecandidateDelegate.invoke(event.candidate);
        };
        this.peerConnection.onicecandidateerror = (event) => {
            if (event.errorCode === 701) {
                // Error 701 indicates that none of the ICE candidates were able to successfully make contact with the STUN or TURN server
                console.info("[multiplayer] onicecandidateerror", event, this.direction);
                return;
            }
            console.warn("[multiplayer] onicecandidateerror", event, this.direction);
        };
        this.peerConnection.ontrack = this.onTrack;
    }
    get connectionState() {
        if (!this.peerConnection)
            return "undefined";
        return this.peerConnection.iceConnectionState;
    }
    updateIceServers(iceServers) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                return;
            }
            const configuration = this.peerConnection.getConfiguration();
            configuration.iceServers = iceServers;
            this.peerConnection.setConfiguration(configuration);
        });
    }
    restartIce(_iceParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                return;
            }
            if (!this.transportReady)
                return;
            this.peerConnection.restartIce();
            this.restartingIce = true;
        });
    }
    rollbackLocalDescription() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection)
                return;
            yield this.peerConnection.setLocalDescription({ type: "rollback" });
        });
    }
    close() {
        this.onConnectDelegate.clear();
        this.onConnectionstatechangeDelegate.clear();
        this.onNegotiationNeededDelegate.clear();
        this.onIcecandidateDelegate.clear();
        this.pendingIceCandidates = [];
        if (!this.peerConnection)
            return;
        this.peerConnection.ontrack = null;
        this.peerConnection.onconnectionstatechange = null;
        this.peerConnection.oniceconnectionstatechange = null;
        this.peerConnection.onnegotiationneeded = null;
        this.peerConnection.onicecandidate = null;
        this.peerConnection.onicecandidateerror = null;
        try {
            this.peerConnection.close();
            this.peerConnection = undefined;
        }
        catch (error) {
            console.log("[multiplayer]", error);
        }
    }
    getNativeRtpCapabilities() {
        return __awaiter(this, void 0, void 0, function* () {
            const pc = new RTCPeerConnection({
                iceServers: [],
                iceTransportPolicy: "all",
                bundlePolicy: "max-bundle",
                rtcpMuxPolicy: "require",
                sdpSemantics: "unified-plan"
            });
            try {
                pc.addTransceiver("audio");
                pc.addTransceiver("video");
                const offer = yield pc.createOffer();
                try {
                    pc.close();
                }
                catch (_a) { }
                const sdpObject = sdpTransform.parse(offer.sdp);
                return extractRtpCapabilities(sdpObject);
            }
            catch (error) {
                try {
                    pc.close();
                }
                catch (_b) { }
                throw error;
            }
        });
    }
    getNativeSctpCapabilities() {
        return __awaiter(this, void 0, void 0, function* () {
            return {
                numStreams: SCTP_NUM_STREAMS
            };
        });
    }
    send(stream, track, encodings, _codecOptions, _codec, codecs, enabledFunc) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("peerConnection undefined");
            }
            this.assertSendDirection();
            const sendingRtpParameters = clone(this.sendingRtpParametersByKind[track.kind], {});
            const enabled = enabledFunc === undefined || enabledFunc();
            const transceiver = this.peerConnection.addTransceiver(track, {
                direction: enabled ? "sendonly" : "inactive",
                streams: [stream],
                sendEncodings: encodings
            });
            if (codecs && "setCodecPreferences" in window.RTCRtpTransceiver.prototype) {
                ;
                transceiver.setCodecPreferences(codecs);
            }
            const originOffer = yield this.peerConnection.createOffer();
            originOffer.sdp = this.changeLocalSdp(originOffer.sdp || "", false);
            const offer = originOffer;
            if (!this.transportReady) {
                yield this.setupTransport();
            }
            if (encodings && encodings.length === 0) {
                encodings.push({});
            }
            yield this.peerConnection.setLocalDescription(originOffer);
            const senderParameters = transceiver.sender.getParameters();
            if (preserveLiveViewResolution(track, senderParameters)) {
                yield transceiver.sender.setParameters(senderParameters);
            }
            if (!this.peerConnection.localDescription) {
                throw new Error("localDescription is null");
            }
            // We can now get the transceiver.mid.
            const localId = transceiver.mid || "";
            // Set MID.
            sendingRtpParameters.mid = localId;
            this.mapMidTransceiver.set(localId, transceiver);
            return {
                localId,
                rtpParameters: sendingRtpParameters,
                rtpSender: transceiver.sender,
                transceiver,
                offer,
                originOffer
            };
        });
    }
    stopSending(localId_1) {
        return __awaiter(this, arguments, void 0, function* (localId, disableTrack = false) {
            if (!this.peerConnection) {
                return;
            }
            this.assertSendDirection();
            const transceiver = this.mapMidTransceiver.get(localId);
            if (!transceiver) {
                throw new Error("associated RTCRtpTransceiver not found");
            }
            transceiver.direction = "inactive";
            if (disableTrack && transceiver.sender.track) {
                transceiver.sender.track.enabled = false;
            }
            yield transceiver.sender.replaceTrack(null);
            transceiver.stop();
            this.peerConnection.removeTrack(transceiver.sender);
            this.mapMidTransceiver.delete(localId);
        });
    }
    stopReceiving(localId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                return;
            }
            this.assertRecvDirection();
            const transceiver = this.mapMidTransceiver.get(localId);
            if (!transceiver)
                throw new Error("associated RTCRtpTransceiver not found");
            this.mapMidTransceiver.delete(localId);
        });
    }
    pauseReceiving(localIds) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.peerConnection) {
                return;
            }
            this.assertRecvDirection();
            for (const localId of localIds) {
                const transceiver = this.mapMidTransceiver.get(localId);
                if (!transceiver)
                    throw new Error("associated RTCRtpTransceiver not found");
                transceiver.direction = "inactive";
            }
            const offer = {
                type: "offer",
                sdp: this.remoteSdp.getSdp()
            };
            offer.sdp = this.changeRemoteSdp(offer.sdp);
            yield this.peerConnection.setRemoteDescription(offer);
            const answer = yield this.peerConnection.createAnswer();
            answer.sdp = this.changeLocalSdp((_a = answer.sdp) !== null && _a !== void 0 ? _a : "", true);
            yield this.peerConnection.setLocalDescription(answer);
        });
    }
    resumeReceiving(localIds) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.peerConnection) {
                return;
            }
            this.assertRecvDirection();
            for (const localId of localIds) {
                const transceiver = this.mapMidTransceiver.get(localId);
                if (!transceiver)
                    throw new Error("associated RTCRtpTransceiver not found");
                transceiver.direction = "recvonly";
            }
            const offer = {
                type: "offer",
                sdp: this.remoteSdp.getSdp()
            };
            offer.sdp = this.changeRemoteSdp(offer.sdp);
            yield this.peerConnection.setRemoteDescription(offer);
            const answer = yield this.peerConnection.createAnswer();
            answer.sdp = this.changeLocalSdp((_a = answer.sdp) !== null && _a !== void 0 ? _a : "", true);
            yield this.peerConnection.setLocalDescription(answer);
        });
    }
    replaceTrack(localId, track) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            const transceiver = this.mapMidTransceiver.get(localId);
            if (!transceiver)
                throw new Error("associated RTCRtpTransceiver not found");
            yield transceiver.sender.replaceTrack(track);
        });
    }
    receive(optionsList) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.peerConnection) {
                throw new Error("peerConnection undefined");
            }
            this.assertRecvDirection();
            const results = [];
            const mapLocalId = new Map();
            let lastSdp;
            for (const options of optionsList) {
                const { trackId, rtpParameters, sdp } = options;
                const localId = (_a = rtpParameters.mid) !== null && _a !== void 0 ? _a : String(this.mapMidTransceiver.size);
                mapLocalId.set(trackId, localId);
                lastSdp = sdp;
            }
            const offer = {
                type: "offer",
                sdp: lastSdp || this.remoteSdp.getSdp()
            };
            offer.sdp = this.changeRemoteSdp(offer.sdp);
            // ontrack is calling after setRemoteDescription
            yield this.peerConnection.setRemoteDescription(offer);
            const originAnswer = yield this.peerConnection.createAnswer();
            originAnswer.sdp = this.changeLocalSdp(originAnswer.sdp || "", true);
            let answer = originAnswer;
            const localSdpObject = sdpTransform.parse(answer.sdp || "");
            answer = {
                type: "answer",
                sdp: originAnswer.sdp || sdpTransform.write(localSdpObject)
            };
            if (!this.transportReady) {
                yield this.setupTransport();
            }
            yield this.peerConnection.setLocalDescription(answer);
            const transceivers = this.peerConnection.getTransceivers();
            for (const transceiver of transceivers) {
                console.log(`[multiplayer] receive transceiver: ${transceiver.mid} dir: ${transceiver.direction} currDir: ${transceiver.currentDirection} kind: ${transceiver.receiver.track.kind} id: ${transceiver.receiver.track.id} sid: ${transceiver.sender.track ? transceiver.sender.track.id : ""}`, transceiver);
            }
            for (const options of optionsList) {
                const { trackId } = options;
                const localId = mapLocalId.get(trackId);
                const transceiver = this.peerConnection.getTransceivers().find((t) => t.mid === localId);
                if (!transceiver || !transceiver.mid) {
                    throw new Error("new RTCRtpTransceiver not found");
                }
                // Store in the map.
                this.mapMidTransceiver.set(localId, transceiver);
                let trackData = this.mapTracks.get(transceiver.mid);
                if (!trackData) {
                    if (transceiver.currentDirection === "inactive") {
                        // onTrack was not called
                        if (transceiver.receiver && transceiver.receiver.track) {
                            trackData = {
                                stream: new MediaStream([transceiver.receiver.track]),
                                track: transceiver.receiver.track
                            };
                            this.mapTracks.set(transceiver.mid, trackData);
                        }
                    }
                    if (!trackData) {
                        throw new Error(`track data with mid ${transceiver.mid} not found`);
                    }
                }
                results.push({
                    localId,
                    stream: trackData.stream,
                    track: transceiver.receiver.track,
                    rtpReceiver: transceiver.receiver,
                    transceiver
                });
            }
            return {
                originAnswer,
                answer,
                results
            };
        });
    }
    setMaxSpatialLayer(localId, spatialLayer) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            const transceiver = this.mapMidTransceiver.get(localId);
            if (!transceiver)
                throw new Error("associated RTCRtpTransceiver not found");
            const parameters = transceiver.sender.getParameters();
            parameters.encodings.forEach((encoding /* RTCRtpEncodingParameters */, idx) => {
                encoding.active = idx <= spatialLayer;
            });
            yield transceiver.sender.setParameters(parameters);
        });
    }
    setRtpEncodingParameters(localId, params) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            const transceiver = this.mapMidTransceiver.get(localId);
            if (!transceiver)
                throw new Error("associated RTCRtpTransceiver not found");
            const parameters = transceiver.sender.getParameters();
            parameters.encodings.forEach((encoding, idx) => {
                parameters.encodings[idx] = Object.assign(Object.assign({}, encoding), params);
            });
            preserveLiveViewResolution(transceiver.sender.track, parameters, params);
            yield transceiver.sender.setParameters(parameters);
        });
    }
    setRtpEncodingParametersByKind(kind, params) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            if (!this.peerConnection)
                return;
            if (!("RTCRtpSender" in window))
                return;
            if (!("setParameters" in window.RTCRtpSender.prototype))
                return;
            if (!("getParameters" in window.RTCRtpSender.prototype))
                return;
            const senders = this.peerConnection.getSenders();
            for (const sender of senders) {
                const { track } = sender;
                if (!track)
                    continue;
                if (track.kind !== kind)
                    continue;
                const parameters = sender.getParameters();
                parameters.encodings.forEach((encoding, idx) => {
                    parameters.encodings[idx] = Object.assign(Object.assign({}, encoding), params);
                });
                preserveLiveViewResolution(track, parameters, params);
                yield sender.setParameters(parameters);
            }
        });
    }
    updateBandwidthRestriction(bitrate_1, priority_1, networkPriority_1) {
        return __awaiter(this, arguments, void 0, function* (bitrate, priority, // RTCPriorityType,
        networkPriority, // RTCPriorityType,
        kind = undefined) {
            if (!this.peerConnection)
                return;
            if (!("RTCRtpSender" in window))
                return;
            if (!("setParameters" in window.RTCRtpSender.prototype))
                return;
            if (!("getParameters" in window.RTCRtpSender.prototype))
                return;
            const senders = this.peerConnection.getSenders();
            for (const sender of senders) {
                const { track } = sender;
                if (!track)
                    continue;
                if (kind != undefined && track.kind !== kind)
                    continue;
                const parameters = sender.getParameters();
                const normalizedBitrate = bitrate !== undefined ? bitrate * 1000 : undefined;
                if (parameters.encodings == null || parameters.encodings.length === 0) {
                    parameters.encodings = [{}];
                }
                let hasChanged = false;
                for (const encoding of parameters.encodings) {
                    if (encoding.maxBitrate !== normalizedBitrate) {
                        hasChanged = true;
                    }
                    if (encoding.priority !== priority) {
                        hasChanged = true;
                    }
                    if (encoding.networkPriority !== networkPriority) {
                        hasChanged = true;
                    }
                    if (normalizedBitrate !== undefined) {
                        encoding.maxBitrate = normalizedBitrate;
                    }
                    else {
                        delete encoding.maxBitrate;
                    }
                    if (priority !== undefined) {
                        ;
                        encoding.priority = priority;
                    }
                    if (networkPriority !== undefined) {
                        ;
                        encoding.networkPriority = networkPriority;
                    }
                }
                if (!hasChanged)
                    return;
                yield sender.setParameters(parameters);
                console.log(`Bitrate changed: ${bitrate}`, track.id);
            }
        });
    }
    createOffer() {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            if (!this.peerConnection) {
                throw new Error("peerConnection is undefined");
            }
            // offerToReceiveAudio and offerToReceiveVideo make problems with Firefox reality
            const offer = yield this.peerConnection.createOffer();
            offer.sdp = this.changeLocalSdp(offer.sdp || "", false);
            yield this.peerConnection.setLocalDescription(offer);
            const desc = this.peerConnection.localDescription;
            if (!desc) {
                throw new Error("createOffer: localDescription is not set");
            }
            return desc;
        });
    }
    getStats() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection)
                return yield Promise.resolve();
            return yield this.peerConnection.getStats();
        });
    }
    checkIsUsingTurn() {
        if (!this.peerConnection)
            return false;
        const receivers = this.peerConnection.getReceivers();
        if (!receivers)
            return false;
        for (const receiver of receivers) {
            if (!receiver.transport)
                continue;
            if (!receiver.transport.iceTransport)
                continue;
            const selectedPair = receiver.transport.iceTransport.getSelectedCandidatePair();
            if (!selectedPair)
                continue;
            const selectedLocalCandidate = selectedPair.local;
            const selectedRemoteCandidate = selectedPair.remote;
            const candidateType = selectedLocalCandidate.candidateType || selectedLocalCandidate.type;
            const isUsingTurn = candidateType === "relay";
            if (isUsingTurn) {
                return true;
            }
            const remoteCanditadeType = selectedRemoteCandidate.candidateType || selectedRemoteCandidate.type;
            if (remoteCanditadeType === "relay") {
                return true;
            }
        }
        return false;
    }
    handleOffer(sdp, mediaId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertRecvDirection();
            if (!this.peerConnection)
                throw new Error("peerConnection is undefined");
            sdp = this.changeRemoteSdp(sdp);
            const desc = new RTCSessionDescription({ type: "offer", sdp });
            try {
                yield this.peerConnection.setRemoteDescription(desc);
            }
            catch (error) {
                console.error(error);
                throw error;
            }
            const answer = yield this.peerConnection.createAnswer();
            answer.sdp = this.changeLocalSdp(answer.sdp || "", true);
            yield this.peerConnection.setLocalDescription(answer);
            const answerDesc = this.peerConnection.localDescription;
            if (!answerDesc) {
                throw new Error("createOffer: localDescription is not set");
            }
            try {
                yield this.applyPendingIceCandidates();
            }
            catch (error) {
                console.error(error);
            }
            const transceivers = this.peerConnection.getTransceivers();
            for (const transceiver of transceivers) {
                console.log(`offer transceiver: ${transceiver.mid} dir: ${transceiver.direction} currDir: ${transceiver.currentDirection} kind: ${transceiver.receiver.track.kind} id: ${transceiver.receiver.track.id}`, transceiver);
            }
            let track;
            if (mediaId) {
                const transceivers = this.peerConnection.getTransceivers();
                const transceiver = transceivers.find((t) => t.mid === mediaId);
                if (!transceiver) {
                    throw new Error("RTCRtpTransceiver not found");
                }
                this.mapMidTransceiver.set(mediaId, transceiver);
                track = transceiver.receiver.track;
            }
            return {
                answer: answerDesc,
                track
            };
        });
    }
    handleAnswer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertSendDirection();
            if (!this.peerConnection)
                return;
            sdp = this.changeRemoteSdp(sdp);
            const answer = { type: "answer", sdp };
            try {
                yield this.peerConnection.setRemoteDescription(answer);
            }
            catch (error) {
                console.error(error);
                throw error;
            }
            try {
                yield this.applyPendingIceCandidates();
            }
            catch (error) {
                console.error(error);
            }
            const transceivers = this.peerConnection.getTransceivers();
            for (const transceiver of transceivers) {
                console.log(`answer transceiver: ${transceiver.mid} dir: ${transceiver.direction} currDir: ${transceiver.currentDirection} kind: ${transceiver.receiver.track.kind}  id: ${transceiver.receiver.track.id}`, transceiver);
            }
        });
    }
    // This can throw
    handleIce(candidate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!candidate)
                return;
            if (!this.peerConnection || !this.peerConnection.remoteDescription || this.peerConnection.remoteDescription.sdp === "") {
                this.pendingIceCandidates.push(candidate);
                return;
            }
            console.log("handleIce", candidate, this.direction);
            yield this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        });
    }
    // this shouldn't throw
    applyPendingIceCandidates() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection)
                return;
            if (this.pendingIceCandidates.length === 0)
                return;
            for (const c of this.pendingIceCandidates) {
                yield this.peerConnection.addIceCandidate(new RTCIceCandidate(c)).catch((error) => {
                    console.error("Error in addIceCandidate", error);
                });
            }
            this.pendingIceCandidates = [];
        });
    }
    setupTransport() {
        return __awaiter(this, void 0, void 0, function* () {
            const dtlsParameters = {};
            yield this.onConnectDelegate.invokeAsPromise(dtlsParameters);
            this.transportReady = true;
        });
    }
    updateCodec(m, codec) {
        if (codec === undefined || codec !== "")
            return;
        const kind = m.type;
        try {
            const [mimeType, channels, clockRate] = codec.split(" ");
            const codecIndex = m.rtp.findIndex((rtp) => {
                if (`${kind}/${rtp.codec}` != mimeType)
                    return false;
                if (rtp.encoding && rtp.encoding != channels)
                    return false;
                return !(rtp.rate && rtp.rate != clockRate);
            });
            if (codecIndex > 0) {
                const preferredCodec = m.rtp[codecIndex];
                m.rtp.splice(codecIndex, 1);
                m.rtp.unshift(preferredCodec);
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    changeLocalSdp(sdp, isAnswer) {
        const opusMaxPlaybackRate = preferencesService.getPreferredValue("opusMaxPlaybackRate", "");
        const opusMaxAverageBitrate = preferencesService.getPreferredValue("opusMaxAverageBitrate", "");
        const opusPtime = preferencesService.getPreferredValue("opusPtime", "");
        const videoGoogleStartBitrate = preferencesService.getPreferredValue("videoGoogleStartBitrate", "");
        const videoGoogleMaxBitrate = preferencesService.getPreferredValue("videoGoogleMaxBitrate", "");
        const videoGoogleMinBitrate = preferencesService.getPreferredValue("videoGoogleMinBitrate", "");
        const audioCodec = preferencesService.getPreferredValue("audioCodec", "");
        const codecOptions = {
            opusStereo: !!preferencesService.getPreferredValue("opusStereo", true),
            opusFec: !!preferencesService.getPreferredValue("opusFec", true),
            opusDtx: !!preferencesService.getPreferredValue("opusDtx", true),
            opusMaxPlaybackRate: opusMaxPlaybackRate === "" || opusMaxPlaybackRate === "unlimited" ? undefined : Number(opusMaxPlaybackRate),
            opusMaxAverageBitrate: opusMaxAverageBitrate === "" || opusMaxAverageBitrate === "unlimited" ? undefined : Number(opusMaxAverageBitrate),
            opusPtime: opusPtime === "" ? undefined : Number(opusPtime),
            videoGoogleStartBitrate: videoGoogleStartBitrate === "" || videoGoogleStartBitrate === "unlimited" ? undefined : Number(videoGoogleStartBitrate),
            videoGoogleMaxBitrate: videoGoogleMaxBitrate === "" || videoGoogleMaxBitrate === "unlimited" ? undefined : Number(videoGoogleMaxBitrate),
            videoGoogleMinBitrate: videoGoogleMinBitrate === "" || videoGoogleMinBitrate === "unlimited" ? undefined : Number(videoGoogleMinBitrate)
        };
        const sdpObj = sdpTransform.parse(sdp || "");
        const codecsMap = new Map();
        for (const m of sdpObj.media) {
            const kind = m.type;
            switch (m.type) {
                case "audio":
                    this.updateCodec(m, audioCodec);
                    // Get codecs.
                    for (const rtp of m.rtp) {
                        const codec = {
                            kind: kind,
                            mimeType: `${kind}/${rtp.codec}`,
                            preferredPayloadType: rtp.payload,
                            clockRate: rtp.rate,
                            channels: rtp.encoding,
                            parameters: {},
                            rtcpFeedback: []
                        };
                        codecsMap.set(codec.preferredPayloadType, codec);
                    }
                    // Get codec parameters.
                    for (const fmtp of m.fmtp || []) {
                        const parameters = sdpTransform.parseParams(fmtp.config);
                        const codec = codecsMap.get(fmtp.payload);
                        if (!codec)
                            continue;
                        switch (codec.mimeType) {
                            case "audio/opus":
                                {
                                    if (codecOptions.opusStereo != undefined) {
                                        if (codecOptions.opusStereo) {
                                            parameters.stereo = 1;
                                            parameters["sprop-stereo"] = 1;
                                        }
                                        else {
                                            parameters.stereo = 0;
                                            parameters["sprop-stereo"] = 0;
                                        }
                                    }
                                    if (codecOptions.opusFec != undefined) {
                                        parameters.useinbandfec = codecOptions.opusFec ? 1 : 0;
                                    }
                                    if (codecOptions.opusDtx != undefined) {
                                        parameters.usedtx = codecOptions.opusDtx ? 1 : 0;
                                    }
                                    // Set Opus maxplaybackrate, if requested.
                                    if (codecOptions.opusMaxPlaybackRate !== undefined) {
                                        parameters.maxplaybackrate = codecOptions.opusMaxPlaybackRate;
                                    }
                                    if (codecOptions.opusMaxAverageBitrate !== undefined) {
                                        parameters.maxaveragebitrate = codecOptions.opusMaxAverageBitrate;
                                    }
                                    if (codecOptions.opusPtime) {
                                        parameters.ptime = codecOptions.opusPtime;
                                    }
                                }
                                break;
                            case "video/vp8":
                            case "video/vp9":
                            case "video/h264":
                            case "video/h265":
                                if (isAnswer) {
                                    if (codecOptions.videoGoogleStartBitrate !== undefined) {
                                        parameters["x-google-start-bitrate"] = codecOptions.videoGoogleStartBitrate;
                                    }
                                    if (codecOptions.videoGoogleMaxBitrate !== undefined) {
                                        parameters["x-google-max-bitrate"] = codecOptions.videoGoogleMaxBitrate;
                                    }
                                    if (codecOptions.videoGoogleMinBitrate !== undefined) {
                                        parameters["x-google-min-bitrate"] = codecOptions.videoGoogleMinBitrate;
                                    }
                                }
                                break;
                        }
                        codec.parameters = parameters;
                        // Write the codec fmtp.config back.
                        fmtp.config = "";
                        for (const key of Object.keys(parameters)) {
                            if (fmtp.config)
                                fmtp.config += ";";
                            fmtp.config += `${key}=${parameters[key]}`;
                        }
                    }
                    // Get RTCP feedback for each codec.
                    for (const fb of m.rtcpFb || []) {
                        const codec = codecsMap.get(fb.payload);
                        if (!codec)
                            continue;
                        const feedback = {
                            type: fb.type,
                            parameter: fb.subtype
                        };
                        if (!feedback.parameter)
                            delete feedback.parameter;
                        codec.rtcpFeedback.push(feedback);
                    }
                    break;
            }
        }
        return sdpTransform.write(sdpObj);
    }
    changeRemoteSdp(sdp) {
        const opusMaxPlaybackRate = preferencesService.getPreferredValue("opusMaxPlaybackRate", "");
        const opusMaxAverageBitrate = preferencesService.getPreferredValue("opusMaxAverageBitrate", "");
        const opusPtime = preferencesService.getPreferredValue("opusPtime", "");
        const videoGoogleStartBitrate = preferencesService.getPreferredValue("opusPtime", "");
        const videoGoogleMaxBitrate = preferencesService.getPreferredValue("opusPtime", "");
        const videoGoogleMinBitrate = preferencesService.getPreferredValue("opusPtime", "");
        const audioCodec = preferencesService.getPreferredValue("audioCodec", "");
        const codecOptions = {
            opusStereo: !!preferencesService.getPreferredValue("opusStereo", true),
            opusFec: !!preferencesService.getPreferredValue("opusFec", true),
            opusDtx: !!preferencesService.getPreferredValue("opusDtx", true),
            opusMaxPlaybackRate: opusMaxPlaybackRate === "" || opusMaxPlaybackRate === "unlimited" ? undefined : Number(opusMaxPlaybackRate),
            opusMaxAverageBitrate: opusMaxAverageBitrate === "" || opusMaxAverageBitrate === "unlimited" ? undefined : Number(opusMaxAverageBitrate),
            opusPtime: opusPtime === "" ? undefined : Number(opusPtime),
            videoGoogleStartBitrate: videoGoogleStartBitrate === "" || videoGoogleStartBitrate === "unlimited" ? undefined : Number(videoGoogleStartBitrate),
            videoGoogleMaxBitrate: videoGoogleMaxBitrate === "" || videoGoogleMaxBitrate === "unlimited" ? undefined : Number(videoGoogleMaxBitrate),
            videoGoogleMinBitrate: videoGoogleMinBitrate === "" || videoGoogleMinBitrate === "unlimited" ? undefined : Number(videoGoogleMinBitrate)
        };
        const sdpObj = sdpTransform.parse(sdp || "");
        const codecsMap = new Map();
        for (const m of sdpObj.media) {
            const kind = m.type;
            switch (m.type) {
                case "audio":
                    this.updateCodec(m, audioCodec);
                    // Get codecs.
                    for (const rtp of m.rtp) {
                        const codec = {
                            kind: kind,
                            mimeType: `${kind}/${rtp.codec}`,
                            preferredPayloadType: rtp.payload,
                            clockRate: rtp.rate,
                            channels: rtp.encoding,
                            parameters: {},
                            rtcpFeedback: []
                        };
                        codecsMap.set(codec.preferredPayloadType, codec);
                    }
                    // Get codec parameters.
                    for (const fmtp of m.fmtp || []) {
                        const parameters = sdpTransform.parseParams(fmtp.config);
                        const codec = codecsMap.get(fmtp.payload);
                        if (!codec)
                            continue;
                        switch (codec.mimeType) {
                            case "audio/opus":
                                {
                                    if (codecOptions.opusStereo != undefined) {
                                        if (codecOptions.opusStereo) {
                                            parameters.stereo = 1;
                                            parameters["sprop-stereo"] = 1;
                                        }
                                        else {
                                            parameters.stereo = 0;
                                            parameters["sprop-stereo"] = 0;
                                        }
                                    }
                                    if (codecOptions.opusStereo)
                                        if (codecOptions.opusFec != undefined) {
                                            parameters.useinbandfec = codecOptions.opusFec ? 1 : 0;
                                        }
                                    if (codecOptions.opusDtx != undefined) {
                                        parameters.usedtx = codecOptions.opusDtx ? 1 : 0;
                                    }
                                    // Set Opus maxplaybackrate, if requested.
                                    if (codecOptions.opusMaxPlaybackRate !== undefined) {
                                        parameters.maxplaybackrate = codecOptions.opusMaxPlaybackRate;
                                    }
                                    if (codecOptions.opusMaxAverageBitrate !== undefined) {
                                        parameters.maxaveragebitrate = codecOptions.opusMaxAverageBitrate;
                                    }
                                    if (codecOptions.opusPtime) {
                                        parameters.ptime = codecOptions.opusPtime;
                                    }
                                }
                                break;
                            case "video/vp8":
                            case "video/vp9":
                            case "video/h264":
                            case "video/h265":
                                if (codecOptions.videoGoogleStartBitrate !== undefined) {
                                    parameters["x-google-start-bitrate"] = codecOptions.videoGoogleStartBitrate;
                                }
                                if (codecOptions.videoGoogleMaxBitrate !== undefined) {
                                    parameters["x-google-max-bitrate"] = codecOptions.videoGoogleMaxBitrate;
                                }
                                if (codecOptions.videoGoogleMinBitrate !== undefined) {
                                    parameters["x-google-min-bitrate"] = codecOptions.videoGoogleMinBitrate;
                                }
                                break;
                        }
                        codec.parameters = parameters;
                        // Write the codec fmtp.config back.
                        fmtp.config = "";
                        for (const key of Object.keys(parameters)) {
                            if (fmtp.config)
                                fmtp.config += ";";
                            fmtp.config += `${key}=${parameters[key]}`;
                        }
                    }
                    // Get RTCP feedback for each codec.
                    for (const fb of m.rtcpFb || []) {
                        const codec = codecsMap.get(fb.payload);
                        if (!codec)
                            continue;
                        const feedback = {
                            type: fb.type,
                            parameter: fb.subtype
                        };
                        if (!feedback.parameter)
                            delete feedback.parameter;
                        codec.rtcpFeedback.push(feedback);
                    }
                    break;
            }
        }
        return sdpTransform.write(sdpObj);
    }
    filterIceCandidate(candidate) {
        return !candidate;
    }
    assertSendDirection() {
        if (this.direction !== "send") {
            throw new Error('method can just be called for handlers with "send" direction');
        }
    }
    assertRecvDirection() {
        if (this.direction !== "recv") {
            throw new Error('method can just be called for handlers with "recv" direction');
        }
    }
}
